//frontend/src/components/avaliacao/BrazaoAvaliacoesButton.jsx
import React, { useMemo } from 'react';
import { Shield } from 'lucide-react';

/**
 * ✅ BrazaoGoogleRating (Elion Version)
 * Exibe: 4,8  🛡️🛡️🛡️🛡️🛡️  (14)
 * * Props:
 * - rating: number (0..5) ex: 4.8
 * - total: number ex: 14
 * - size: number (default 16) - Levemente maior para leitura
 * - showTotal: boolean
 * - variant: 'gold' | 'brand' | 'neutral'
 */
export default function BrazaoGoogleRating({
  rating = 0,
  total = 0,
  max = 5,
  size = 16,
  showTotal = true,
  variant = 'gold', // Padrão 'gold' para destacar avaliação
  className = '',
}) {
  // Garante range 0..5
  const safeRating = useMemo(() => {
    const r = typeof rating === 'number' && !Number.isNaN(rating) ? rating : 0;
    return Math.max(0, Math.min(max, r));
  }, [rating, max]);

  // Lógica Google: Arredonda para o 0.5 mais próximo
  // Ex: 4.2 -> 4.0 | 4.3 -> 4.5 | 4.7 -> 4.5 | 4.8 -> 5.0
  const steps = useMemo(() => {
    const rounded = Math.round(safeRating * 2) / 2;
    const full = Math.floor(rounded);
    const half = rounded % 1 !== 0 ? 1 : 0;
    const empty = max - full - half;
    return { full, half, empty };
  }, [safeRating, max]);

  // Label numérico (ex: "4,8")
  const label = useMemo(() => {
    return safeRating.toFixed(1).replace('.', ',');
  }, [safeRating]);

  // Sistema de Cores
  const colors = useMemo(() => {
    const baseText = 'text-slate-900'; // Cor do número (Escuro/Legível)
    const baseTotal = 'text-slate-500'; // Cor do total (Discreto)

    switch (variant) {
      case 'brand': // Azul Elion
        return {
          number: baseText,
          filled: 'text-blue-600 fill-blue-600',
          empty: 'text-slate-200',
          total: baseTotal,
        };
      case 'neutral': // Cinza Minimalista
        return {
          number: baseText,
          filled: 'text-slate-700 fill-slate-700',
          empty: 'text-slate-200',
          total: baseTotal,
        };
      case 'gold': // Padrão "Review" (Recomendado)
      default:
        return {
          number: baseText,
          filled: 'text-amber-400 fill-amber-400', // Dourado Google
          empty: 'text-slate-200',
          total: baseTotal,
        };
    }
  }, [variant]);

  return (
    <div
      className={['inline-flex items-center gap-1.5', className].join(' ')}
      title={`Avaliação: ${label} de 5`}
    >
      {/* 1. Nota (Destaque visual como no Google) */}
      <span className={['text-sm font-bold', colors.number].join(' ')}>
        {label}
      </span>

      {/* 2. Ícones (Brasões) */}
      <span className="inline-flex items-center gap-[1px]" aria-hidden="true">
        {/* Full Shields */}
        {Array.from({ length: steps.full }).map((_, i) => (
          <Shield
            key={`f-${i}`}
            size={size}
            strokeWidth={2}
            className={colors.filled}
          />
        ))}

        {/* Half Shield (Comportamento Visual Perfeito) */}
        {steps.half === 1 && (
          <span
            className="relative inline-block"
            style={{ width: size, height: size }}
          >
            {/* Fundo (Vazio) */}
            <Shield size={size} strokeWidth={2} className={colors.empty} />
            {/* Frente (Metade Cheia - Recorte) */}
            <span
              className="absolute top-0 left-0 overflow-hidden"
              style={{ width: '50%', height: '100%' }}
            >
              <Shield size={size} strokeWidth={2} className={colors.filled} />
            </span>
          </span>
        )}

        {/* Empty Shields */}
        {Array.from({ length: steps.empty }).map((_, i) => (
          <Shield
            key={`e-${i}`}
            size={size}
            strokeWidth={2}
            className={colors.empty}
          />
        ))}
      </span>

      {/* 3. Total de Avaliações */}
      {showTotal && (
        <span className={['text-xs', colors.total].join(' ')}>({total})</span>
      )}
    </div>
  );
}
